import React from "react";
import ReactApexChart from "react-apexcharts";
import { THEME_COLORS } from "../../../../../color";

const LineAndColumnGraph = ({
  title,
  categories = [],
  seriesData = [],
  yAxisLeftConfig = {},
  yAxisRightConfig = {},
  height = 350,
  showLegends,
  columnWidth = "16px",
  paddingLeft = 9,
  paddingRight = 15,
  graphWidth = "100%",
}) => {
  const validSeriesData = Array.isArray(seriesData) ? seriesData : [];

  const columnColors = [
    THEME_COLORS.green,
    THEME_COLORS.yellow,
    THEME_COLORS.lightblue, // 2019
    // 2020
    THEME_COLORS.pink,
    THEME_COLORS.darkblue, // 2021
    // 2022
    // 2023
  ];

  const chartOptions = {
    chart: {
      type: "line",
      height,
      toolbar: { show: false },
      zoom: { enabled: false },
      offsetX: 0,
      offsetY: 0,
    },
    grid: {
      show: false,
      padding: {
        left: paddingLeft, // Add left padding to create gap from left Y-axis
        right: paddingRight, // Add right padding to create gap from right Y-axis
        top: 10,
        bottom: 10,
      },
    },
    legend: {
      show: false,
    },
    stroke: {
      width: [0, 2.5],
      colors: [THEME_COLORS.green, "#E2A928"],
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0, 1],
      offsetY: -10,
      background: {
        enabled: true,
        foreColor: "#fff",
        padding: 4,
        borderRadius: 2,
        borderWidth: 0,
        dropShadow: {
          enabled: false,
        },
      },
      style: {
        fontSize: "10px",
        fontWeight: "normal",
        colors: ["#333"],
      },
      formatter: function (value, opts) {
        if (value === null || value === undefined) return "";
        const formattedVal = parseFloat(value.toFixed(2));
        if (opts.seriesIndex === 0) {
          return formattedVal;
        } else {
          return `${formattedVal}%`;
        }
      },
    },
    markers: {
      offsetY: 0,
      size: [0, 5],
      colors: ["#fff", "#fff"],
      strokeColors: ["#06513D", "#E2A928"],
      strokeWidth: 3,
    },
    xaxis: {
      categories,
      axisBorder: {
        show: true,
        color: "#000",
      },
      labels: {
        style: {
          fontSize: "11px",
          color: "#000",
          fontWeight: "normal",
        },
      },
      offsetX: 0,
      offsetY: 0,
    },
    yaxis: [
      {
        seriesName: validSeriesData[0]?.name || "Employee Count",
        axisBorder: { show: true, color: "#000", offsetY: -2 },
        title: {
          text: yAxisLeftConfig?.label || "Left Axis",
          style: {
            color: "#000",
            fontWeight: "normal",
            fontSize: "11px",
          },
        },
        min: yAxisLeftConfig?.min || 0,
        max: yAxisLeftConfig?.max || 1000,
        tickAmount: yAxisLeftConfig?.tickAmount || 10,
        labels: {
          formatter: (value) => `${value}`,
          offsetX: -5, // Move labels slightly away from axis
          style: {
            color: "#000",
            fontWeight: "normal",
            fontSize: "11px",
          },
        },
      },
      {
        seriesName: validSeriesData[1]?.name || "Turnover",
        axisBorder: { show: true, color: "#000", offsetY: -2 },
        opposite: true,
        title: {
          text: yAxisRightConfig?.label || "Right Axis",
          style: {
            color: "#000",
            fontWeight: "normal",
            fontSize: "11px",
          },
        },
        min: yAxisRightConfig?.min || 0,
        max: yAxisRightConfig?.max !== undefined ? yAxisRightConfig.max : 100,
        tickAmount: yAxisRightConfig?.tickAmount || 10,
        labels: {
          formatter: (value) => `${value}%`,
          offsetX: 5, // Move labels slightly away from axis
          style: {
            color: "#000",
            fontWeight: "normal",
            fontSize: "11px",
          },
        },
      },
    ],
    plotOptions: {
      bar: {
        columnWidth: columnWidth,
        distributed: true,
        borderRadius: 2,
        dataLabels: {
          position: "bottom",
        },
      },
      line: {
        lineWidth: "2px",
      },
    },
    colors: columnColors,
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: { height: 300 },
          dataLabels: { style: { fontSize: "8px" } },
          grid: {
            padding: {
              left: 15,
              right: 15,
              top: 10,
              bottom: 10,
            },
          },
        },
      },
      {
        breakpoint: 480,
        options: {
          grid: {
            padding: {
              left: 10,
              right: 10,
              top: 5,
              bottom: 5,
            },
          },
        },
      },
    ],
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
  };

  return (
    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 w-full">
      {/* Graph Container */}
      <div className="flex-1 w-full mx-auto" style={{ maxWidth: graphWidth }}>
        <ReactApexChart
          options={chartOptions}
          series={validSeriesData}
          type="line"
          height={height}
        />
        <p className="text-center mt-2 text-sm md:text-base text-gray-700">
          Years
        </p>
        {/* Custom Legend at the bottom */}
        <div className="flex justify-center items-center gap-6 mt-4 pb-2">
          {validSeriesData[0] && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#06513D] rounded-sm" />
              <span className="text-[12px] font-medium text-gray-700 font-calibri">
                {validSeriesData[0].name}
              </span>
            </div>
          )}
          {validSeriesData[1] && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#E2A928] rounded-sm" />
              <span className="text-[12px] font-medium text-gray-700 font-calibri">
                {validSeriesData[1].name}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      {showLegends && (
        <div className="flex flex-wrap md:flex-col gap-2 md:gap-3 md:pl-4">
          {categories.map((year, index) => (
            <div key={year} className="flex items-center gap-2">
              <div
                className="w-4 h-4 md:w-5 md:h-5 rounded"
                style={{ backgroundColor: columnColors[index] }}
              />
              <span className="text-sm">{year}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LineAndColumnGraph;
